import { create } from "zustand";

import type { PortalRole } from "@/shared/types/auth";

// JS-readable hint that a session was established on this device. The refresh
// cookie itself is HttpOnly (invisible to JS), so we use this flag to decide
// whether a reload should even attempt POST /auth/refresh — skipping it when the
// user never logged in avoids a benign-but-noisy 401 on the login screen.
export const SESSION_HINT_KEY = "shc.medical.hasSession";

export function hasSessionHint(): boolean {
	try {
		return localStorage.getItem(SESSION_HINT_KEY) !== null;
	} catch {
		return false;
	}
}

export function clearSessionHint(): void {
	try {
		localStorage.removeItem(SESSION_HINT_KEY);
	} catch {
		/* storage unavailable — nothing to clear */
	}
}

interface AuthState {
	/** Access token — stored in memory only (never persisted per AGENT_RULES). */
	token: string | null;
	isAuthenticated: boolean;
	email: string;
	name: string;
	role: PortalRole;
	/**
	 * Set while the session came in through the hospital EMR (SHCT-994): the one patient it may view, read-only.
	 * The server enforces the confinement (api-contracts/emr-bridge.md §B.1.1); the UI only follows it.
	 */
	emrPatientUid: string | null;
	login: (token: string, email: string, name: string, role: PortalRole) => void;
	/**
	 * EMR session: writes no session hint — there is no refresh cookie, so a reload simply ends it (plan D9④). An
	 * existing hint is left alone: it may be another signed-in tab's (localStorage is shared across tabs).
	 */
	loginEmr: (email: string, name: string, role: PortalRole, patientUid: string) => void;
	logout: () => void;
}

const initialState = {
	token: null,
	isAuthenticated: false,
	email: "",
	name: "",
	role: "DOCTOR" as PortalRole,
	emrPatientUid: null,
};

export const useAuthStore = create<AuthState>((set, get) => ({
	...initialState,
	login: (token, email, name, role) => {
		try {
			localStorage.setItem(SESSION_HINT_KEY, "1");
		} catch {
			/* storage unavailable — refresh will just attempt unconditionally */
		}
		set({ token, isAuthenticated: true, email, name, role, emrPatientUid: null });
	},
	loginEmr: (email, name, role, patientUid) => {
		set({ token: "", isAuthenticated: true, email, name, role, emrPatientUid: patientUid });
	},
	logout: () => {
		// An EMR session never wrote the hint, so the one present (if any) is another tab's — keep it.
		if (get().emrPatientUid === null) clearSessionHint();
		set(initialState);
	},
}));

/** Institution-wide data scope: NURSE and CRC see all patients; DOCTOR only their own. */
export const useAuthCanViewAll = () =>
	useAuthStore((s) => s.role === "NURSE" || s.role === "CRC");

/**
 * Write access. Every medical-staff role may record: CRC has the same permissions as a nurse, read and
 * write alike — 공통 시스템정의 (Common System Definition) PRD 11 §3.3 [MED.007], restated in PRD 03 §7 and
 * PRD 04 §7 (SHCT-956). CRC was previously excluded here, which is the defect that ticket reports.
 *
 * The narrower per-action rules are separate and unchanged: 관리 종료 (close management) and its revert stay
 * 본인 담당만 (own-assigned only) through the care team, and 중재 기록 편집 (intervention record edit) stays
 * 본인 작성분만 (own-authored only) through the author check in RecordForm. The server re-checks both.
 *
 * An EMR session (SHCT-994) writes nothing, whatever the role — the server answers every write with 403.
 */
const _useAuthCanWrite = () =>
	useAuthStore(
		(s) =>
			s.emrPatientUid === null &&
			(s.role === "DOCTOR" || s.role === "NURSE" || s.role === "CRC"),
	);

/** EMR-born session (SHCT-994): view-only — every write and every CSV export is closed. */
export const useIsEmrSession = () => useAuthStore((s) => s.emrPatientUid !== null);

export const useAuthCanWriteIntervention = _useAuthCanWrite;

export const useAuthCanRegisterPatient = _useAuthCanWrite;
